/**
 * Context-aware wire-up between the Brain's memory/artefact store and the
 * three direct "Generate with AI" buttons (Philosophy, Session Planner,
 * Curriculum). Every generator should:
 *
 *   1. call buildGenerationContext(...) to fetch:
 *      - brain_memories relevant to the coach + team + entity
 *      - existing artefacts on the same entity (principles, recent
 *        sessions, active curriculum)
 *      - a pre-rendered markdown-ish block ready to paste into the prompt
 *   2. inject `ctx.block` into its prompt as <priors>...</priors>
 *   3. after the coach accepts/saves the generated output, call
 *      recordGenerationObservation(...) which writes a brain_memories row
 *      (kind='reflection') so the next Brain turn sees what was produced.
 *
 * Keeping two entry points (Brain conversation + one-click buttons) is
 * deliberate — coaches want both — but their context + learning loops
 * are shared via this module.
 */
import { supabase } from '@4cm/core/supabase';
import { createMemory, searchMemories, type BrainMemory } from './brainMemory';

export type GeneratorEntity = 'philosophy' | 'session' | 'curriculum';

export interface GenerationContext {
  memories: BrainMemory[];
  existing_artefacts: Record<string, unknown>;
  /** Pre-rendered <priors> block — paste verbatim into the generator
   *  prompt. Empty string when there's no context to share yet. */
  block: string;
  /** True when we have actual prior artefacts (principles, sessions,
   *  curriculum) — useful for callers that want to switch tone
   *  ("refining" vs "from scratch"). */
  has_existing: boolean;
}

export interface BuildGenerationContextArgs {
  coachId: string;
  teamId: string | null;
  entity: GeneratorEntity;
  /** Extra tags the memory search should bias toward (e.g. the name of
   *  the primary focus for a session). Optional. */
  biasTags?: string[];
  /** Cap on memory count. Defaults to 8 — plenty for a prompt, well
   *  below the DB fetch ceiling. */
  memoryLimit?: number;
}

export async function buildGenerationContext(args: BuildGenerationContextArgs): Promise<GenerationContext> {
  const { coachId, teamId, entity, biasTags, memoryLimit } = args;
  const limit = memoryLimit ?? 8;

  const [memories, existing_artefacts] = await Promise.all([
    searchMemories({
      coach_id: coachId,
      team_id: teamId,
      tags: buildSearchTags(entity, biasTags),
      limit,
    }),
    fetchExistingArtefacts(entity, teamId),
  ]);

  const has_existing = existingArtefactsNonEmpty(existing_artefacts);
  const block = renderBlock(entity, memories, existing_artefacts);

  return { memories, existing_artefacts, block, has_existing };
}

// ---------- observation write-back ----------

export interface RecordGenerationObservationArgs {
  coachId: string;
  teamId: string | null;
  entity: GeneratorEntity;
  /** One-sentence summary of what was generated. Shown back to the
   *  Brain on the next turn. Keep ≤ 160 chars. */
  summary: string;
  /** Optional saved-row id (e.g. curriculum_plans.id). Stored in
   *  evidence.ref_id for traceability. */
  refId?: string | null;
  /** Raw fragment to help future prompts recall what this was (first
   *  80 chars of the principle text, the session title, etc.). */
  excerpt?: string;
  extraTags?: string[];
}

export async function recordGenerationObservation(args: RecordGenerationObservationArgs): Promise<BrainMemory | null> {
  const { coachId, teamId, entity, summary, refId, excerpt, extraTags } = args;
  return createMemory({
    coach_id: coachId,
    team_id: teamId,
    scope: 'team',
    entity_id: null,
    kind: 'reflection',
    fact: summary,
    tags: Array.from(new Set([`entity:${entity}`, 'source:direct-gen', ...(extraTags ?? [])])),
    evidence: {
      source: 'direct_generator',
      ref_id: refId ?? undefined,
      excerpt: excerpt?.slice(0, 80),
    },
    confidence: 0.7,
  });
}

// ---------- internal helpers ----------

function buildSearchTags(entity: GeneratorEntity, biasTags?: string[]): string[] {
  // Include the entity tag so memories intentionally tagged for this
  // generator bubble to the top. Carry anything the caller wants biased.
  // Also include cross-cutting tags the Brain already uses for tactical
  // style so philosophy/curriculum/session all benefit from one store.
  const base = [
    `entity:${entity}`,
    'topic:style',
    'topic:philosophy',
    'preference:author-style',
  ];
  return Array.from(new Set([...base, ...(biasTags ?? [])]));
}

async function fetchExistingArtefacts(
  entity: GeneratorEntity,
  teamId: string | null,
): Promise<Record<string, unknown>> {
  if (!teamId) return {};
  switch (entity) {
    case 'philosophy': {
      const { data } = await supabase
        .from('principles')
        .select('id, type, text, corner_ids')
        .eq('team_id', teamId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true });
      return { principles: data ?? [] };
    }
    case 'session': {
      // Recent-sessions context for the planner: what corners has the team
      // already been hitting? Pull last 5 sessions with their coverage
      // and objectives so the generator can vary rather than repeat.
      //
      // Also pull the most recent completed session_reviews — their
      // next_session_tweaks drop verbatim into the priors block, and any
      // drill that graded struggled/<3 is flagged so the planner avoids
      // re-scheduling it. Feedback loop closed.
      const [sessionsRes, principlesRes, curriculumRes, reviewsRes, poorDrillsRes] = await Promise.all([
        supabase
          .from('sessions')
          .select('id, date, objectives, phase_coverage, corner_coverage, psych_social_intent')
          .eq('team_id', teamId)
          .is('deleted_at', null)
          .order('date', { ascending: false })
          .limit(5),
        supabase
          .from('principles')
          .select('type, text, corner_ids')
          .eq('team_id', teamId)
          .is('deleted_at', null),
        supabase
          .from('curriculum_plans')
          .select('plan_data, season_start, season_end')
          .eq('team_id', teamId)
          .eq('is_active', true)
          .maybeSingle(),
        supabase
          .from('session_reviews')
          .select('session_id, notes_working, notes_issues, next_session_tweaks, completed_at, sessions!inner(team_id, date)')
          .eq('sessions.team_id', teamId)
          .eq('status', 'complete')
          .order('completed_at', { ascending: false })
          .limit(3),
        // Drills that graded struggled or <3/5 in the last 14 days —
        // pass their names forward so the planner can dodge them.
        supabase
          .from('drill_grades')
          .select('sentiment, grade, session_drill:session_drills!inner(session_id, drill:drills!inner(id, name, team_id))')
          .eq('session_drill.drill.team_id', teamId)
          .or('sentiment.eq.struggled,grade.lt.3')
          .order('updated_at', { ascending: false })
          .limit(20),
      ]);
      return {
        recent_sessions: sessionsRes.data ?? [],
        principles: principlesRes.data ?? [],
        active_curriculum: curriculumRes.data ?? null,
        recent_reviews: reviewsRes.data ?? [],
        poor_drills: poorDrillsRes.data ?? [],
      };
    }
    case 'curriculum': {
      const [principlesRes, curriculumRes] = await Promise.all([
        supabase
          .from('principles')
          .select('type, text, corner_ids')
          .eq('team_id', teamId)
          .is('deleted_at', null),
        supabase
          .from('curriculum_plans')
          .select('plan_data, season_start, season_end, is_active')
          .eq('team_id', teamId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      return {
        principles: principlesRes.data ?? [],
        prior_curriculum: curriculumRes.data ?? null,
      };
    }
  }
}

function existingArtefactsNonEmpty(existing: Record<string, unknown>): boolean {
  for (const v of Object.values(existing)) {
    if (Array.isArray(v) && v.length > 0) return true;
    if (v && typeof v === 'object' && !Array.isArray(v)) return true;
  }
  return false;
}

function renderBlock(
  entity: GeneratorEntity,
  memories: BrainMemory[],
  existing: Record<string, unknown>,
): string {
  const lines: string[] = [];
  const hasMem = memories.length > 0;
  const hasArt = existingArtefactsNonEmpty(existing);
  if (!hasMem && !hasArt) return '';

  lines.push('<priors>');
  lines.push(
    "Context carried over from the Brain's memory store and the coach's existing work. Treat as soft priors — respect what's there, fill gaps, and flag conflicts in your output rather than silently overwriting.",
  );
  lines.push('');

  if (hasMem) {
    lines.push('<relevant_memories>');
    for (const m of memories) {
      const tags = m.tags?.length ? ` [${m.tags.slice(0, 4).join(', ')}]` : '';
      lines.push(`  - [${m.scope} · ${m.kind} · ${m.confidence.toFixed(2)}] ${m.fact}${tags}`);
    }
    lines.push('</relevant_memories>');
    lines.push('');
  }

  switch (entity) {
    case 'philosophy':
      renderPhilosophyArtefacts(existing, lines);
      break;
    case 'session':
      renderSessionArtefacts(existing, lines);
      break;
    case 'curriculum':
      renderCurriculumArtefacts(existing, lines);
      break;
  }

  lines.push('</priors>');
  return lines.join('\n');
}

function renderPhilosophyArtefacts(existing: Record<string, unknown>, lines: string[]) {
  const principles = (existing.principles as Array<{ type: string; text: string }> | undefined) ?? [];
  if (principles.length === 0) return;
  lines.push('<existing_principles>');
  for (const p of principles) {
    lines.push(`  - [${p.type}] ${p.text}`);
  }
  lines.push('</existing_principles>');
  lines.push('  Rule: keep principles consistent with these. If you propose new ones, they must complement — not contradict — the list above. If a conflict is unavoidable, tag the proposal with note="conflicts with <existing principle>".');
  lines.push('');
}

function renderSessionArtefacts(existing: Record<string, unknown>, lines: string[]) {
  const sessions =
    (existing.recent_sessions as Array<{ date: string; objectives: string[]; phase_coverage?: Record<string, number> | null; corner_coverage?: Record<string, number> | null; psych_social_intent: string }> | undefined) ?? [];
  const principles =
    (existing.principles as Array<{ type: string; text: string }> | undefined) ?? [];
  const curriculum =
    (existing.active_curriculum as { plan_data?: { season_overview?: string; phase_plans?: Array<{ phase: string; theme: string }> } } | null | undefined) ?? null;
  const reviews =
    (existing.recent_reviews as Array<{ notes_working?: string | null; notes_issues?: string | null; next_session_tweaks?: string | null; completed_at?: string | null; sessions?: { date?: string } | Array<{ date?: string }> }> | undefined) ?? [];
  const poorDrills =
    (existing.poor_drills as Array<{ sentiment?: string | null; grade?: number | null; session_drill?: { drill?: { name?: string } | Array<{ name?: string }> } | Array<{ drill?: { name?: string } | Array<{ name?: string }> }> }> | undefined) ?? [];

  if (principles.length > 0) {
    lines.push('<team_principles>');
    for (const p of principles) lines.push(`  - [${p.type}] ${p.text}`);
    lines.push('</team_principles>');
    lines.push('');
  }
  if (curriculum?.plan_data) {
    lines.push('<active_curriculum>');
    if (curriculum.plan_data.season_overview) lines.push(`  Season overview: ${curriculum.plan_data.season_overview}`);
    if (curriculum.plan_data.phase_plans?.length) {
      lines.push('  Phase plans:');
      for (const ph of curriculum.plan_data.phase_plans) lines.push(`    - ${ph.phase}: ${ph.theme}`);
    }
    lines.push('</active_curriculum>');
    lines.push('');
  }
  if (sessions.length > 0) {
    lines.push('<recent_sessions>');
    lines.push('  The last five sessions already run. Avoid repeating phase-of-play themes back-to-back — vary the primary focus relative to these.');
    for (const s of sessions) {
      const phaseCov = s.phase_coverage
        ? Object.entries(s.phase_coverage).map(([k, v]) => `${k.slice(0, 2)}=${v}%`).join(' ')
        : '';
      const cornerCov = s.corner_coverage
        ? Object.entries(s.corner_coverage).map(([k, v]) => `${k.slice(0, 2)}=${v}%`).join(' ')
        : '';
      const obj = (s.objectives ?? []).slice(0, 2).join(' / ');
      lines.push(`  - ${s.date}: ${obj}${phaseCov ? ` (phase: ${phaseCov})` : ''}${cornerCov ? ` (corner: ${cornerCov})` : ''}`);
      if (s.psych_social_intent) lines.push(`      psych/social intent: ${s.psych_social_intent}`);
    }
    lines.push('</recent_sessions>');
    lines.push('');
  }
  if (reviews.length > 0) {
    lines.push('<recent_session_reviews>');
    lines.push("  The coach's own notes after recent sessions. CARRY THE TWEAKS FORWARD verbatim where they make sense — that's the point of reviewing.");
    for (const r of reviews) {
      const sessionRef = Array.isArray(r.sessions) ? r.sessions[0] : r.sessions;
      const date = sessionRef?.date ?? '(unknown date)';
      lines.push(`  - ${date}:`);
      if (r.notes_working) lines.push(`      worked: ${r.notes_working.slice(0, 200)}`);
      if (r.notes_issues) lines.push(`      issues: ${r.notes_issues.slice(0, 200)}`);
      if (r.next_session_tweaks) lines.push(`      TWEAKS TO APPLY: ${r.next_session_tweaks.slice(0, 300)}`);
    }
    lines.push('</recent_session_reviews>');
    lines.push('');
  }
  if (poorDrills.length > 0) {
    const names = new Set<string>();
    for (const pd of poorDrills) {
      const sd = Array.isArray(pd.session_drill) ? pd.session_drill[0] : pd.session_drill;
      const drill = sd && (Array.isArray(sd.drill) ? sd.drill[0] : sd.drill);
      if (drill?.name) names.add(drill.name);
    }
    if (names.size > 0) {
      lines.push('<drills_to_avoid>');
      lines.push('  These drills graded `struggled` or below 3/5 in the last two weeks — pick alternatives with the same corner intent rather than rescheduling these.');
      for (const n of Array.from(names).slice(0, 10)) lines.push(`  - ${n}`);
      lines.push('</drills_to_avoid>');
      lines.push('');
    }
  }
}

function renderCurriculumArtefacts(existing: Record<string, unknown>, lines: string[]) {
  const principles =
    (existing.principles as Array<{ type: string; text: string }> | undefined) ?? [];
  const prior =
    (existing.prior_curriculum as { plan_data?: { season_overview?: string; phase_plans?: Array<{ phase: string; theme: string; corner_emphasis: Record<string, number> }> }; season_start?: string; season_end?: string; is_active?: boolean } | null | undefined) ?? null;

  if (principles.length > 0) {
    lines.push('<team_principles>');
    for (const p of principles) lines.push(`  - [${p.type}] ${p.text}`);
    lines.push('</team_principles>');
    lines.push('  Rule: the curriculum must route the team through every principle at least once across its phase plans.');
    lines.push('');
  }
  if (prior?.plan_data) {
    const label = prior.is_active ? 'active_curriculum' : 'previous_curriculum';
    lines.push(`<${label}>`);
    if (prior.plan_data.season_overview) lines.push(`  Season overview: ${prior.plan_data.season_overview}`);
    if (prior.plan_data.phase_plans?.length) {
      for (const ph of prior.plan_data.phase_plans) {
        const cov = ph.corner_emphasis
          ? Object.entries(ph.corner_emphasis).map(([k, v]) => `${k}=${v}%`).join(' ')
          : '';
        lines.push(`  - ${ph.phase}: ${ph.theme}${cov ? ` (${cov})` : ''}`);
      }
    }
    lines.push(`</${label}>`);
    if (prior.is_active) {
      lines.push('  Rule: if the coach is regenerating, refine — carry forward strong elements of the active plan rather than starting from scratch.');
    }
    lines.push('');
  }
}
